import { useMemo } from 'react';
import styled from 'styled-components';

import { AccessModes, Dimensions, Metrics, Sources } from 'components/insights/constants';
import MetricTile from 'components/insights/layout/tiles/MetricTile';
import DonutChart from 'components/shared/charts/DonutChart';
import { ReviewType } from 'models/Review';
import { useNetworkPerformanceActions, useNetworkPerformanceStore } from 'stores/networkPerformanceStore';
import { colors } from 'styles/theme/colors';

import useNetworkPerformanceAnalytics from '../query/useNetworkPerformanceAnalytics';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from '../slideout/constants';

const REFUSED_BY_COLORS: Record<string, string> = {
  patient: colors.red25,
  rehab_facility: colors.red100,
};

export default function ServiceRefusalsTile() {
  const { openSlideout } = useNetworkPerformanceActions();
  const selectedGroupType = useNetworkPerformanceStore((state) => state.selectedGroupType);

  const queryParams = useMemo(
    () => ({
      source: Sources.REVIEWS,
      accessModes: [AccessModes.MANAGER],
      reviewType: ReviewType.SERVICE_REFUSAL,
      dimensions: [Dimensions.SERVICE_REFUSAL_REFUSED_BY_TYPE],
      metrics: [Metrics.SERVICE_REFUSALS_COUNT],
      rollups: true,
    }),
    []
  );

  const { loading, data, rollups } = useNetworkPerformanceAnalytics(queryParams, {
    queryKey: 'networkPerformanceServiceRefusals',
  });

  const totalRefusals = rollups.overall.metrics.getCurrentDataPoint(Metrics.SERVICE_REFUSALS_COUNT, 0) ?? 0;

  const refusedByTypes = data.dimensions.getCurrentDimensionValuesByIndex(0);
  const refusedByCounts = data.metrics.getCurrentSeries(Metrics.SERVICE_REFUSALS_COUNT);

  const providerLabel = selectedGroupType?.displayName ?? 'Provider';

  const chartData = useMemo(() => {
    const items = refusedByTypes.map((type, i) => ({
      type,
      name: type === 'patient' ? 'Refused by Patient' : `Refused by ${providerLabel}`,
      y: refusedByCounts[i] ?? 0,
      color: REFUSED_BY_COLORS[type] ?? colors.black25,
    }));
    // Provider (darker) first, patient (lighter) second
    return items.sort((a, b) => (a.type === 'patient' ? 1 : -1) - (b.type === 'patient' ? 1 : -1));
  }, [refusedByTypes, refusedByCounts, providerLabel]);

  const hasData = totalRefusals > 0;

  return (
    <MetricTile
      label={METRIC_CONFIGS[NetworkPerformanceMetrics.SERVICE_REFUSALS].label}
      showValue={false}
      loading={loading}
      hasData={hasData}
      onClick={() => openSlideout(NetworkPerformanceMetrics.SERVICE_REFUSALS)}>
      <Container>
        <ChartWrapper>
          <DonutChart
            data={chartData}
            centerValue={totalRefusals}
            centerLabel=''
            config={{
              chart: { height: 116, width: 116 },
              title: {
                text: `<div style="text-align:center;"><b style="font-size:20px;font-weight:700;">${totalRefusals}</b></div>`,
                y: 16,
              },
              legend: { enabled: false },
              plotOptions: {
                pie: { size: 116, innerSize: 66, center: ['50%', '50%'] },
              },
            }}
          />
        </ChartWrapper>
        <LegendList>
          {chartData.map((item) => {
            const pct = totalRefusals > 0 ? ((item.y / totalRefusals) * 100).toFixed(0) : '0';
            return (
              <LegendRow key={item.name}>
                <LegendLeft>
                  <LegendDot $color={item.color} />
                  <LegendName>{item.name}</LegendName>
                </LegendLeft>
                <LegendValue>
                  <b>{pct}%</b> <LegendCount>({item.y})</LegendCount>
                </LegendValue>
              </LegendRow>
            );
          })}
        </LegendList>
      </Container>
    </MetricTile>
  );
}

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ChartWrapper = styled.div`
  flex-shrink: 0;
  width: 116px;
`;

const LegendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LegendLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const LegendName = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.black75};
`;

const LegendValue = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.black100};
`;

const LegendCount = styled.span`
  color: ${({ theme }) => theme.colors.black50};
`;
